import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { AppSettings } from 'src/app/app-settings';
import { BondsServices } from 'src/app/services/bonds.service';
import { PassSelectDialogComponent } from '../pass-select-dialog/pass-select-dialog.component';
import { PassPurchaseComponent } from '../pass-purchase/pass-purchase.component';
import { fixedPassFromLabel, isFixedPricePass } from 'src/app/utils/fixed-price-pass.util';
import { passUnavailableMessage } from 'src/app/utils/pass-availability.util';

@Component({
  selector: 'app-pass-detail',
  templateUrl: './pass-detail.component.html',
  styleUrls: ['./pass-detail.component.scss']
})
export class PassDetailComponent implements OnInit, OnDestroy {

  pass: any = null;
  loaded: boolean = false;
  notFound: boolean = false;
  // Por qué el abono ya no se puede completar (null si todavía se puede)
  unavailableMessage: string | null = null;
  urlImages: string = AppSettings.MEDIA_URI + '/events_images/';
  private sub: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private bondsService: BondsServices,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.sub = this.route.params.subscribe(params => {
      const id = +params['id'];
      if (id) {
        this.loadPass(id);
      } else {
        this.notFound = true;
        this.loaded = true;
      }
    });
  }

  loadPass(id: number) {
    this.bondsService.getPassById(id).subscribe({
      next: (res) => {
        this.pass = res?.discount || res?.data || res;
        if (!this.pass || !this.pass.active) {
          this.notFound = true;
        } else {
          this.pass.events = (this.pass.pass_items || []).map((item: any) => item.event).filter((e: any) => !!e);
          this.unavailableMessage = passUnavailableMessage(this.pass);
        }
        this.loaded = true;
      },
      error: () => {
        this.notFound = true;
        this.loaded = true;
      }
    });
  }

  get isFixedPrices(): boolean {
    return isFixedPricePass(this.pass);
  }

  /** Precio fijo: lo más barato del abono en un evento (su nivel más barato) */
  getFixedEventFrom(event: any): number | null {
    const prices = (this.pass?.pass_prices || [])
      .filter((p: any) => +p.event_id === +event.id)
      .map((p: any) => +p.price);
    return prices.length ? Math.min(...prices) : null;
  }

  /** Precio fijo: total del abono por zona, solo las que tienen precio en todos los eventos */
  get fixedTotalsByLevel(): { name: string; total: number }[] {
    return (this.pass?.price_summary?.by_level || [])
      .filter((row: any) => row.total != null)
      .map((row: any) => ({ name: row.name, total: +row.total }));
  }

  getDiscountBadge(): string {
    if (!this.pass) return '';
    if (this.isFixedPrices) return fixedPassFromLabel(this.pass);
    if (this.pass.discount_mode === 'percentage') {
      return `${Math.trunc(this.pass.discount_value)}% descuento`;
    }
    return `${this.pass.discount_value}€ descuento`;
  }

  getBondTicket(event: any): any {
    if (!event.tickets) return null;
    return event.tickets.find((t: any) => t.active && !t.only_tpv_sales);
  }

  getOriginalPrice(event: any): number | null {
    if (!event.tickets) return null;
    const bondTicket = this.getBondTicket(event);
    if (!bondTicket) return null;
    const bondPrice = +bondTicket.price;
    if (this.pass.discount_mode === 'percentage') {
      return bondPrice / (1 - (+this.pass.discount_value / 100));
    } else {
      return bondPrice + (+this.pass.discount_value);
    }
  }

  hasMap(event: any): boolean {
    return event.space?.maps?.length > 0 || event.id_map != null;
  }

  startPurchase() {
    const selectRef = this.dialog.open(PassSelectDialogComponent, {
      panelClass: ['info-dialog', 'pass-select-dialog-panel'],
      data: { passes: [this.pass] }
    });
    selectRef.afterClosed().subscribe((result: any) => {
      if (result?.pass) {
        this.dialog.open(PassPurchaseComponent, {
          panelClass: ['info-dialog', 'not-padding', 'pass-purchase-dialog-panel'],
          width: '860px',
          maxWidth: '95vw',
          data: { passId: result.pass.id, quantity: result.quantity || 1 }
        });
      }
    });
  }

  goBack() {
    this.router.navigate(['/abonos']);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
